/**
 * @fileoverview Toggle widget — pass/fail capable.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render a binary switch with optional on/off labels, PASS/FAIL coloring
 *   driven by `extras.failsOn` for failure-capable toggles, and N/A bypass.
 * - Modern iOS/iPadOS-style switch visuals (rounded pill, spring transition,
 *   soft shadow on the thumb) since inspectors primarily use tablets.
 *
 * Dependencies:
 * - React 18 (peer).
 * - `@firesquirrel/core-domain` `getFailingMappings` for legacy mapped
 *   toggle fallback.
 *
 * Architecture:
 * - Stateless presentational component. Parent owns the value via `onChange`.
 *
 * @component ToggleField
 */
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import { getPassFailStatus } from '../violations/passFailState';

export function ToggleField(props: BaseWidgetProps<boolean | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, bulkPilotMode, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { onLabel?: string; offLabel?: string };
  const checked = value === true;

  // Pass/fail is owned solely by `extras.hasPassFail` + `extras.failsOn`.
  // Violation code bindings are metadata for citation/capture, not the source
  // of truth for which side of the switch means FAIL. See `passFailState.ts`.
  const passFailStatus = getPassFailStatus(field, value, isNA);
  // iOS/iPadOS-style flat, saturated palette — matches the native UISwitch
  // "on" green so the widget feels at home on the tablets inspectors carry.
  const trackColorClass = passFailStatus === 'fail'
    ? 'bg-[#FF3B30]' // iOS red
    : passFailStatus === 'pass'
      ? 'bg-[#34C759]' // iOS green
      : passFailStatus === 'neutral'
        ? 'bg-[#D1D1D6]' // iOS light gray (unanswered)
        : value === true
          ? 'bg-[#34C759]' // iOS green ("Yes")
          : value === false
            ? 'bg-[#FF9500]' // iOS orange ("No" — answered, not just unanswered)
            : 'bg-[#D1D1D6]';

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="toggle"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      passFailStatus={passFailStatus}
      errorMessage={errorMessage}
      requiredUnmet={requiredUnmet}
    >
      <div className="flex items-center gap-2">
        {extras.offLabel && <span className="text-xs text-gray-500">{extras.offLabel}</span>}
        <button
          id={field.id}
          type="button"
          role="switch"
          aria-checked={checked}
          aria-required={!!(field.required || resolved?.required)}
          disabled={!!resolved?.disabled || bulkPilotMode}
          onClick={() => onChange(!checked)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full ring-1 ring-inset ring-black/5 shadow-inner transition-colors duration-200 ease-in-out disabled:opacity-50 ${trackColorClass}`}
        >
          <span
            className="inline-block h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition-transform duration-200 ease-in-out"
            style={{ transform: checked ? 'translateX(22px)' : 'translateX(2px)' }}
          />
        </button>
        {extras.onLabel && <span className="text-xs text-gray-500">{extras.onLabel}</span>}
      </div>
    </FieldWrapper>
  );
}
